import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { numberToWords } from "../Inovice/utils";
import { formatNumberWithCommas } from "../../utils";

export function PDFGenerator(data, fileName = "tax_invoice.pdf") {
  const placeholder = "N/A";
  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  // Constants for layout
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const footerReserve = 55;

  // Fixed header dimensions
  const logoHeight = 25;
  const logoTopMargin = margin;
  const billToBoxTop = logoTopMargin + logoHeight + 5;
  const trnBoxHeight = 10;
  let billToBoxHeight = 50;
  let trnBoxTop = billToBoxTop + billToBoxHeight;
  let headerEndY = trnBoxTop + trnBoxHeight + 3;

  // Helper function to wrap text
  const wrapText = (text, width) => doc.splitTextToSize(text || placeholder, width);

  // ==================== HEADER SECTION (on every page) ====================
  const drawHeader = () => {
    const colWidth = contentWidth / 3;
    const rowHeight = 4;

    // Logo
    doc.addImage(
      "/Trigon_Logo_UAE-1.png",
      "PNG",
      margin,
      logoTopMargin,
      80,
      logoHeight
    );

    // TAX INVOICE title - right aligned
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("TAX INVOICE", pageWidth - margin, logoTopMargin + 15, {
      align: "right",
    });

    const billToLines = [
      data?.billTo?.num || placeholder,
      data?.billTo?.name || placeholder,
      data?.billTo?.city || placeholder,
      `PO BOX: ${data?.billTo?.postal || placeholder}`,
      data?.billTo?.street || placeholder,
      // data?.billTo?.address || placeholder,
    ];
    const shipToLines = [
      data?.shipTo?.num || placeholder,
      data?.shipTo?.name || placeholder,
      data?.shipTo?.city || placeholder,
      `PO BOX: ${data?.shipTo?.postal || placeholder}`,
      data?.shipTo?.street || placeholder,
      // data?.shipTo?.address || placeholder,
    ];
    const invoiceDetails = [
      { label: "INVOICE NO", value: data?.invoiceNumber?.[0] || placeholder },
      { label: "DATE", value: data?.invoiceDate || placeholder },
      { label: "SALES REP", value: data?.salesRep || placeholder },
      { label: "PAYMENT", value: data?.paymentTerms || placeholder },
      { label: "PO REF NO", value: data?.poRefNo || placeholder },
      { label: "DELIVERY NO", value: data?.deliveryNo || placeholder },
    ];

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    const countWrappedLines = (lines, width) =>
      lines.reduce((sum, line) => sum + wrapText(line, width).length, 0);

    const boxPaddingTop = 4;
    const boxPaddingBottom = 1;
    const billToLineCount = 1 + countWrappedLines(billToLines, colWidth - 6);
    const shipToLineCount = 1 + countWrappedLines(shipToLines, colWidth - 6);
    const invoiceLineCount = invoiceDetails.length;
    const maxLineCount = Math.max(billToLineCount, shipToLineCount, invoiceLineCount);

    billToBoxHeight = boxPaddingTop + boxPaddingBottom + maxLineCount * rowHeight;
    trnBoxTop = billToBoxTop + billToBoxHeight;
    headerEndY = trnBoxTop + trnBoxHeight + 3;

    // Bill To / Ship To / Invoice Details Box
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, billToBoxTop, contentWidth, billToBoxHeight);

    // Draw vertical dividers
    doc.line(margin + colWidth, billToBoxTop, margin + colWidth, billToBoxTop + billToBoxHeight);
    doc.line(margin + colWidth * 2, billToBoxTop, margin + colWidth * 2, billToBoxTop + billToBoxHeight);

    // BILL TO Section
    let y1 = billToBoxTop + boxPaddingTop;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("BILL TO:", margin + 3, y1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    y1 += rowHeight;

    billToLines.forEach((line) => {
      const lines = wrapText(line, colWidth - 6);
      lines.forEach((wrappedLine) => {
        doc.text(wrappedLine, margin + 3, y1);
        y1 += rowHeight;
      });
    });

    // SHIP TO Section
    let y2 = billToBoxTop + boxPaddingTop;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("SHIP TO:", margin + colWidth + 3, y2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    y2 += rowHeight;

    shipToLines.forEach((line) => {
      const lines = wrapText(line, colWidth - 6);
      lines.forEach((wrappedLine) => {
        doc.text(wrappedLine, margin + colWidth + 3, y2);
        y2 += rowHeight;
      });
    });

    // Invoice Details Section (right column) - Two column layout
    let y3 = billToBoxTop + boxPaddingTop;
    const labelX = margin + colWidth * 2 + 3;
    const valueX = margin + colWidth * 2 + 30;
    doc.setFontSize(7);

    invoiceDetails.forEach((detail) => {
      doc.setFont("helvetica", "bold");
      doc.text(detail.label, labelX, y3);
      doc.setFont("helvetica", "normal");
      const wrappedValue = wrapText(detail.value, colWidth - 35);
      doc.text(wrappedValue[0] || placeholder, valueX, y3);
      y3 += rowHeight;
    });

    // TRN Section
    doc.rect(margin, trnBoxTop, contentWidth, trnBoxHeight);
    doc.line(margin + colWidth, trnBoxTop, margin + colWidth, trnBoxTop + trnBoxHeight);
    doc.line(margin + colWidth * 2, trnBoxTop, margin + colWidth * 2, trnBoxTop + trnBoxHeight);

    const trnY = trnBoxTop + 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(`TRN: ${data?.billTo?.trn || placeholder}`, margin + 3, trnY);
    doc.text(`TRN: ${data?.shipTo?.trn || placeholder}`, margin + colWidth + 3, trnY);
    doc.text(`TRIGON TRN: ${data?.trigonTRN || placeholder}`, margin + colWidth * 2 + 3, trnY);

    return headerEndY;
  };

  // ==================== FOOTER SECTION (on every page) ====================
  const drawFooter = (pageNum, totalPages) => {
    const footerLineY = pageHeight - 38;
    const authorizedY = footerLineY - 2;
    const footerTextStartY = footerLineY + 4;
    const footerLineHeight = 3.5;
    const certifiedY = authorizedY - 8;
    const forTrigonY = authorizedY - 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(
      "Certified that the particulars given above are true and correct",
      margin,
      certifiedY
    );
    doc.text("FOR TRIGON LLC", margin, forTrigonY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("AUTHORIZED SIGNATORY", margin, authorizedY);

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, footerLineY, pageWidth - margin, footerLineY);

    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    doc.text("Bank Details:", pageWidth / 2, footerTextStartY, { align: "center" });

    doc.setFont("helvetica", "normal");
    const bankDetails = [
      "Trigon LLC, Emirates NBD, Al Maktoum Road Branch, Dubai, U.A.E.",
      "Dirham Account No.: 101 1087569 701, Dirham IBAN No.: AE500260001011087569701",
      "Dollar Account No.: 102 1087569 702, Dollar IBAN No.: AE580260001021087569702",
      "Swift Code: EBILAEADFTS",
      "Payment to be made by crossed cheques in favour of Trigon L.L.C.",
      "Website : www.trigononline.com",
    ];

    bankDetails.forEach((line, index) => {
      doc.text(
        line,
        pageWidth / 2,
        footerTextStartY + (index + 1) * footerLineHeight,
        { align: "center" }
      );
    });

    // Page number
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Page No:${pageNum}`, margin, pageHeight - 8);
  };

  // ==================== ITEMS TABLE ====================
  const drawItemsTable = (startY) => {
    const headers = [
      "S.NO",
      "ITEM",
      "DESCRIPTION",
      "QTY",
      "RATE",
      "DISC",
      "Price After\nDiscount",
      "VAT\nRate%",
      "VAT\nAmount",
      "Total\nAmount",
    ];

    const rows = data?.invoiceItems?.map((item, index) => [
      index + 1,
      item.itemCode || placeholder,
      item.description || placeholder,
      item.quantity || placeholder,
      formatNumberWithCommas(item.rate) || placeholder,
      item.discount || placeholder,
      formatNumberWithCommas(item.priceAfterDiscount) || placeholder,
      item.vatPercentage || placeholder,
      formatNumberWithCommas(item.vatAmount) || placeholder,
      formatNumberWithCommas(item.totalAmount) || placeholder,
    ]) || [];

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: startY,
      margin: {
        top: headerEndY,
        left: margin,
        right: margin,
        bottom: footerReserve,
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 24 },
        2: { cellWidth: 36 },
        3: { cellWidth: 12, halign: "center" },
        4: { cellWidth: 16, halign: "right" },
        5: { cellWidth: 12, halign: "center" },
        6: { cellWidth: 20, halign: "right" },
        7: { cellWidth: 12, halign: "center" },
        8: { cellWidth: 16, halign: "right" },
        9: { cellWidth: 22, halign: "right" },
      },
      didDrawPage: function () {
        drawHeader();
      },
      showHead: "everyPage",
    });

    return doc.lastAutoTable.finalY;
  };

  // ==================== TOTALS SECTION ====================
  const drawTotalsSection = (startY) => {
    let yPos = startY + 5;
    const colWidth = contentWidth / 2;
    const rowHeight = 5.5;

    const totalsRows = [
      ["Total Amount Before Tax:", formatNumberWithCommas(data?.totals?.totalBeforeTax ?? placeholder)],
      ["Total Retention Amount:", formatNumberWithCommas(data?.totals?.retentionAmount ?? placeholder)],
      ["Total VAT Amount after Retention:", formatNumberWithCommas(data?.totals?.totalVatAfterRetention ?? placeholder)],
      ["Advance Received:", formatNumberWithCommas(data?.totals?.advanceReceived ?? placeholder)],
      ["Balance Amount Before Tax:", formatNumberWithCommas(data?.totals?.balanceAmountBeforeTax ?? placeholder)],
      ["Tax on Advance:", formatNumberWithCommas(data?.totals?.taxOnAdvance ?? placeholder)],
      ["Balance Tax Amount:", formatNumberWithCommas(data?.totals?.balanceTaxAmount ?? placeholder)],
      ["Balance Amount After Tax:", formatNumberWithCommas(data?.totals?.finalAmount ?? placeholder)],
    ];

    const sectionHeight = totalsRows.length * rowHeight + 4;

    // Check if we need a new page for totals
    const descriptionHeight = 8;
    const totalsHeight = sectionHeight + 5 + descriptionHeight;
    if (yPos + totalsHeight > pageHeight - footerReserve) {
      doc.addPage();
      drawHeader();
      yPos = headerEndY + 5;
    }

    // Draw boxes
    doc.setLineWidth(0.2);
    doc.rect(margin, yPos, colWidth, sectionHeight);
    doc.rect(margin + colWidth, yPos, colWidth, sectionHeight);

    // Amount in Words (left box)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("AMOUNT IN WORDS:", margin + 3, yPos + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);

    const amountValue = data?.totals?.finalAmount;
    const words = amountValue !== undefined && amountValue !== null ? "AED " + numberToWords(amountValue) : placeholder;
    const splitWords = doc.splitTextToSize(words, colWidth - 8);
    let wordY = yPos + 10;
    splitWords.forEach((line) => {
      doc.text(line, margin + 3, wordY);
      wordY += 4;
    });

    // Totals table (right box)
    const leftLabelX = margin + colWidth + 3;
    const rightValueX = margin + contentWidth - 3;
    let totalsY = yPos + 5;

    doc.setFontSize(6);

    totalsRows.forEach((row, i) => {
      if (i > 0) {
        doc.line(
          margin + colWidth,
          yPos + (i * rowHeight) + 1.5,
          margin + contentWidth,
          yPos + (i * rowHeight) + 1.5
        );
      }

      doc.setFont("helvetica", "bold");
      doc.text(row[0], leftLabelX, totalsY);
      doc.setFont("helvetica", "normal");
      doc.text(row[1], rightValueX, totalsY, { align: "right" });
      totalsY += rowHeight;
    });

    const dividerX = margin + colWidth + colWidth * 0.65;
    doc.line(dividerX, yPos, dividerX, yPos + sectionHeight);

    yPos += sectionHeight + 5;

    // Description
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`Description: ${data?.description || placeholder}`, margin, yPos);

    return yPos;
  };

  // ==================== GENERATE PDF ====================
  const tableStartY = drawHeader();
  const tableEndY = drawItemsTable(tableStartY);
  drawTotalsSection(tableEndY);

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  // Save the PDF
  doc.save(fileName);
}
